import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnChanges, OnDestroy, ViewChild, AfterViewInit } from '@angular/core';
import { Chart, ChartConfiguration, ChartDataset, registerables } from 'chart.js';
import { color as chartColor } from 'chart.js/helpers';
import { DateTime } from 'luxon';

import { roundToPrecision } from '@cf-shared/shared-util';
import { TrendDirection } from '@cf-shared/running-trend';
import { PROVISIONAL_DASH, TREND_DIRECTION_COLOR_VARS, TREND_TUNNEL_FILL_ALPHA } from 'components/cs-progress-chart/progress-chart-trend';
import { LL } from 'i18n/i18n.service';
import { calculateMinMax, readChartTheme } from 'utils/chart-helpers';
import { readingMarkerRadii } from './trend-band-markers';

/** one reading, placed by its calendar day */
export interface TrendBandChartPoint {
  dateIso: string;
  value: number;
}

/**
 * The trend at one day. A row with no numbers is a GAP: the line and the tunnel break there,
 * which is how a stretch with too few readings is shown.
 */
export interface TrendBandChartRow {
  dateIso: string;
  low?: number;
  mid?: number;
  high?: number;
  /** the running trend's direction here (#6627); when rows carry one, the tunnel is tinted by it */
  direction?: TrendDirection;
  /** true while a later reading can still move this day (#6627); the line is dashed there */
  provisional?: boolean;
}


/**
 * A line chart for the Body Composition overview (#5456): the readings as markers, a trend
 * line, and a shaded tunnel between a low and a high line, drawn like the Progress page's chart
 * (#6676).
 *
 * It only DRAWS. Every number it shows is handed in already worked out (see
 * `shared/src/body-comp-overview.ts`), and values are in whatever unit the host converted them
 * to. It is deliberately separate from `cs-trend-chart`, which fits its own 2-point trend over
 * the last 7 days and must stay exactly as it is for the screens that use it.
 */
@Component({
  selector: 'cs-trend-band-chart',
  templateUrl: 'cs-trend-band-chart.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CsTrendBandChartComponent implements OnChanges, AfterViewInit, OnDestroy {
  protected LL = LL;

  /** the readings, drawn as markers */
  @Input() points: TrendBandChartPoint[] = [];
  /** the trend, one row per day, oldest first; a row with no numbers breaks the line */
  @Input() band: TrendBandChartRow[] = [];
  /** line and marker colour, as a CSS colour string the host read from the theme */
  @Input() color = '';
  /** the first and last calendar day of the x axis; when missing the data's own span is used */
  @Input() firstDayIso?: string;
  @Input() lastDayIso?: string;
  /** the unit written after a value in the tooltip; values arrive already converted to it */
  @Input() unitName = '';
  /** how a reading's day is written in the tooltip; the host passes the app's date formatter */
  @Input() formatDate: (dateIso: string) => string = dateIso => dateIso;

  @ViewChild('chartCanvas', { static: true }) canvasElement: ElementRef;

  /** true when there is no reading to draw; worked out from the input, so it is right on the first render */
  protected get isEmpty(): boolean {
    return !(this.points || []).some(p => !!p && Number.isFinite(p.value));
  }

  private chart: Chart | undefined;
  private viewReady = false;

  constructor(private cd: ChangeDetectorRef, private el: ElementRef<HTMLElement>) {
    Chart.register(...registerables);
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.redraw();
  }

  ngOnChanges(): void {
    if (this.viewReady) {
      this.redraw();
    }
  }

  ngOnDestroy(): void {
    this.chart?.destroy();
    this.chart = undefined;
  }

  /** throw the old chart away and draw a new one from the current inputs */
  private redraw(): void {
    const points = (this.points || []).filter(p => !!p && Number.isFinite(p.value));
    const rows = (this.band || []).filter(row => !!row?.dateIso);

    this.chart?.destroy();
    this.chart = new Chart(this.canvasElement.nativeElement as HTMLCanvasElement, this.buildConfig(points, rows));
    this.cd.markForCheck();
  }

  /**
   * the chart.js configuration, drawn the way `cs-progress-chart` draws its lines (#6676): the
   * readings as hollow markers, a 2 px trend line, the tunnel between a low and a high line, a
   * y axis padded over all of them, and tick labels and grid in the chart tokens
   */
  private buildConfig(points: TrendBandChartPoint[], rows: TrendBandChartRow[]): ChartConfiguration {
    const styles = getComputedStyle(document.documentElement);
    // a host that passes no colour gets the theme's first chart colour
    const color = this.color || styles.getPropertyValue('--chart-purple').trim();
    // the shared chart tokens, and the tick labels in the host's own font, as cs-progress-chart's are
    const theme = readChartTheme(this.el.nativeElement);

    // a row with no number becomes a null y, which chart.js draws as a gap. the time scale
    // parses the ISO day itself, so x is handed over as the string
    const lineData = (pick: (row: TrendBandChartRow) => number | undefined) => rows.map(row => {
      const value = pick(row);
      return { x: row.dateIso as unknown as number, y: (Number.isFinite(value) ? value : null) as number };
    });

    const hiddenLine = { pointRadius: 0, pointHoverRadius: 0, pointHitRadius: 0, borderWidth: 0, tension: 0, spanGaps: false };
    // rows from the running trend carry a direction and a provisional mark; rows from the straight
    // fit and the interim running trend carry neither and draw exactly as before
    const hasDirections = rows.some(row => row.direction !== undefined);
    const directionFill = (index: number) => this.translucent(styles.getPropertyValue(TREND_DIRECTION_COLOR_VARS[rows[index]?.direction ?? TrendDirection.Uncertain]).trim(), TREND_TUNNEL_FILL_ALPHA);
    const lineSegment = { borderDash: (ctx: { p1DataIndex: number }) => rows[ctx.p1DataIndex]?.provisional ? PROVISIONAL_DASH : undefined };
    const fillSegment = { backgroundColor: (ctx: { p0DataIndex: number }) => directionFill(ctx.p0DataIndex) };
    // the axis spans the readings AND the tunnel and the trend, so neither is ever clipped
    const yRange = calculateMinMax([
      ...points.map(p => p.value),
      ...rows.flatMap(row => [row.low, row.mid, row.high]).filter(value => Number.isFinite(value)),
    ]);
    const spanDays = this.firstDayIso && this.lastDayIso
      ? DateTime.fromISO(this.lastDayIso).diff(DateTime.fromISO(this.firstDayIso), 'days').days
      : 0;
    const datasets: ChartDataset<'line'>[] = [
      // 0: the readings, as the Progress chart's hollow markers; with many readings only those in
      // a gap of the trend line show, and a hidden one still answers the pointer
      {
        data: points.map(p => ({ x: p.dateIso as unknown as number, y: p.value })),
        showLine: false,
        pointRadius: readingMarkerRadii(points, rows, theme.pointRadius),
        pointHoverRadius: theme.pointHoverRadius,
        pointHitRadius: 6,
        pointBorderWidth: theme.pointBorderWidth,
        pointBackgroundColor: theme.pointBackground,
        pointBorderColor: color,
      },
      // 1: the trend line
      { ...hiddenLine, data: lineData(row => row.mid), borderColor: color, borderWidth: 2, fill: false, ...(hasDirections ? { segment: lineSegment } : {}) },
      // 2: the top of the tunnel, filled down to dataset 3; each stretch takes its direction's tint when rows have one
      {
        ...hiddenLine,
        data: lineData(row => row.high),
        borderColor: 'transparent',
        backgroundColor: this.translucent(color),
        fill: 3,
        ...(hasDirections ? { segment: fillSegment } : {}),
      },
      // 3: the bottom of the tunnel
      { ...hiddenLine, data: lineData(row => row.low), borderColor: 'transparent', fill: false },
    ];

    return {
      type: 'line',
      // the line-typed datasets are what a 'line' chart takes; the configuration's own type is the union of every chart type
      data: { datasets: datasets as ChartConfiguration['data']['datasets'] },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        // room for a marker that sits on the first or last day, which the chart area would otherwise cut in half
        layout: { padding: 6 },
        interaction: { mode: 'nearest', axis: 'x', intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: {
            displayColors: false,
            // only the readings answer the pointer; the lines are context
            filter: item => item.datasetIndex === 0,
            callbacks: {
              title: items => items.length > 0 ? this.formatDate(String((items[0].raw as { x: unknown }).x)) : '',
              label: context => `${roundToPrecision(context.parsed.y, 0.1).toFixed(1)} ${this.unitName}`.trim(),
            },
          },
        },
        scales: {
          x: {
            type: 'time',
            min: this.firstDayIso,
            max: this.lastDayIso,
            // up to about a month, day numbers; longer ranges, month names (as the Progress chart)
            time: spanDays <= 31
              ? { unit: 'day', minUnit: 'day', displayFormats: { day: 'd' } }
              : { unit: 'month', minUnit: 'day', displayFormats: { month: 'MMM' } },
            ticks: { color: theme.tick, font: theme.font, maxRotation: 0, minRotation: 0, autoSkip: true, maxTicksLimit: 5 },
            grid: { display: false },
            border: { display: false },
          },
          y: {
            min: yRange.min,
            max: yRange.max,
            ticks: { color: theme.tick, font: theme.font, maxTicksLimit: 4, callback: value => roundToPrecision(Number(value), 0.1).toFixed(1) },
            grid: { color: theme.grid, lineWidth: 1 },
            border: { display: false },
          },
        },
      },
    };
  }

  /** the tunnel's fill: the line colour at low opacity */
  private translucent(color: string, alpha = 0.22): string {
    return chartColor(color).alpha(alpha).rgbString();
  }
}
